import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import React from 'react';
import { getAllCountries } from 'services/countryServices';
import { getAllLanguages } from 'services/languageServices';
import { fetchAllProducts } from 'services/productServices';
import { getContainers } from 'services/selfServiceContainerServices';
import {
  CountryDto,
  LanguageDto,
  ProductDto,
  SelfServiceContainerDto,
  TreatmentBookingDto,
  UpsertPatientDto
} from 'ts-types/api.types';

interface State {
  patientId?: number;
  patient?: UpsertPatientDto;

  countries: CountryDto[];
  languages: LanguageDto[];
  containers: SelfServiceContainerDto[];
  products: ProductDto[];

  // the current persisted (in the DB) state of the treatment booking
  treatmentBookingCurrent?: Partial<TreatmentBookingDto>
  setTreatmentBookingCurrent: (booking: TreatmentBookingDto) => void;

  patientEntryContextRefDataFetched: boolean;
  fetchPatientEntryContextRefData: (cancelTokenSource: CancelTokenSource) => void;
}

const PatientEntryContext = React.createContext({
  countries: [] as CountryDto[],
  languages: [] as LanguageDto[],
  containers: [] as SelfServiceContainerDto[],
  products: [] as ProductDto[],

  setTreatmentBookingCurrent: (booking: TreatmentBookingDto) => {
  },

  patientEntryContextRefDataFetched: false,
  fetchPatientEntryContextRefData: (cancelTokenSource: CancelTokenSource) => {
  }
});

interface Props extends JSX.ElementChildrenAttribute, AuthConsumerRenderProps {
}

class PatientEntryContextProvider extends React.Component<Props, State> {

  cancelTokenSource = axios.CancelToken.source();

  constructor(props: any) {
    super(props);

    this.state = {
      countries: [],
      languages: [],
      containers: [],
      products: [],

      setTreatmentBookingCurrent: this.setTreatmentBookingCurrent,

      patientEntryContextRefDataFetched: false,
      fetchPatientEntryContextRefData: this.fetchPatientEntryContextRefData
    };

    this.fetchPatientEntryContextRefData(this.cancelTokenSource);
  }

  componentWillUnmount(): void {
    this.cancelTokenSource.cancel('Component unmounted.');
  }

  setTreatmentBookingCurrent = (booking: TreatmentBookingDto) => {
    this.setState({
      treatmentBookingCurrent: booking
    });
  };

  fetchPatientEntryContextRefData = (cancelTokenSource: CancelTokenSource): void => {
    const {currentUser} = this.props;
    const mandateId = currentUser!.userConfig.mandateId;

    Promise.all([
          getAllCountries(cancelTokenSource),
          getAllLanguages(cancelTokenSource),
          getContainers(mandateId, cancelTokenSource),
          fetchAllProducts(false, cancelTokenSource)
        ])
        .then(result => {
          this.setState({
            countries: result[0],
            languages: result[1],
            containers: result[2],
            products: result[3],
            patientEntryContextRefDataFetched: true
          })
        });
  };

  render() {
    return (
        <PatientEntryContext.Provider value={this.state}>
          {this.props.children}
        </PatientEntryContext.Provider>
    )
  }
}

export const PatientEntryContextProviderWrapper = withAuthContext(PatientEntryContextProvider);

export const PatientEntryConsumer = PatientEntryContext.Consumer;

export interface PatientEntryConsumerRenderProps extends State {
}

export const withPatientEntryContext = (Component: typeof React.Component | React.FunctionComponent<any>) => {
  return (props: any) => (
      <PatientEntryConsumer>
        {({...patientEntryProps}: PatientEntryConsumerRenderProps) => {
          return <Component {...props} {...patientEntryProps} />
        }}
      </PatientEntryConsumer>
  );
};
